import omit from 'lodash/omit';
import uniq from 'lodash/uniq';

import {showNewNotification} from '@edna/components';

import {TData, TPayload} from 'src/components/List/useList';
import {TItemId} from 'src/constants';
import {TItem as TParametersItem} from 'src/containers/Parameters/definitions';
import {TItem as TTagsItem} from 'src/containers/Tags/definitions';
import {ECacheTag, listenerMiddleware, rootApi} from 'src/models';

import {
  EAddress,
  TAddressItem,
  TAutoGeneratedTag,
  TFilters,
  TSubscriber,
  TSubscriberSaveRequest,
  TSubscribersConfirmPayload,
  TSubscribersUploadPreview,
  TSubscribersUploadStatus,
} from './definitions';

export const SUBSCRIBERS_UPLOAD_STATUS_POLLING_INTERVAL = 3_000;

const api = rootApi.injectEndpoints({
  overrideExisting: 'throw',
  endpoints: (builder) => ({
    uploadSubscribers: builder.mutation<TSubscribersUploadPreview, File>({
      query: (file) => {
        const formData = new FormData();

        formData.append('file', file);

        return {
          method: 'POST',
          url: `/subscriber/upload`,
          data: formData,
          meta: {isShowError: true},
        };
      },
      invalidatesTags: [ECacheTag.SUBSCRIBERS],
    }),
    confirmSubscribers: builder.mutation<TAutoGeneratedTag | undefined, TSubscribersConfirmPayload>(
      {
        query: (data) => ({
          method: 'POST',
          url: '/subscriber/upload/confirm-and-tag',
          data,
          meta: {isShowError: true},
        }),
      },
    ),
    getSubscribersUploadStatus: builder.query<TSubscribersUploadStatus, TItemId>({
      query: (id) => ({
        method: 'GET',
        url: `/subscriber/upload/status/${id}`,
      }),
    }),
    getSubscribers: builder.query<TData<TSubscriber>, TPayload<TFilters>>({
      query: (payload) => ({
        url: `/subscriber/list`,
        method: 'POST',
        data: {
          ...omit(payload, 'parameters', 'blacklistTag'),
          variables: payload.parameters,
          tags: uniq(
            [payload.blacklistTag?.id]
              .concat(payload.tags?.map((tag: TTagsItem) => tag.id))
              .filter(Boolean),
          ),
        },
      }),
      providesTags: (result) => {
        if (!result) {
          return [ECacheTag.SUBSCRIBERS];
        }

        return [
          ECacheTag.SUBSCRIBERS,
          ...result.content.map(({id}) => ({id, type: ECacheTag.SUBSCRIBERS})),
        ];
      },
    }),
    saveSubscriber: builder.mutation<TSubscriber, TSubscriberSaveRequest>({
      query: (item) => {
        return {
          url: !!item.id ? `/subscriber/${item.id}` : `/subscriber/`,
          method: !!item.id ? 'PUT' : 'POST',
          data: item,
        };
      },
      invalidatesTags: (_, __, {id}) => [{id, type: ECacheTag.SUBSCRIBERS}],
    }),
    getSubscriber: builder.query<TSubscriber, TItemId>({
      query: (id) => ({
        url: `/subscriber/${id}`,
        method: 'GET',
        meta: {isShowError: true},
      }),
      providesTags: (_, __, id) => [{id, type: ECacheTag.SUBSCRIBERS}],
    }),
    getSubscriberByAddress: builder.query<
      {id: TItemId; blacklisted: boolean},
      {
        address: string;
        type: EAddress;
      }
    >({
      query: (data) => ({
        method: 'POST',
        url: `/subscriber/by-address`,
        data,
        meta: {isShowError: true, errorMessageKey: 'List:errors.failedToLoadItem'},
      }),
    }),
    deleteSubscriber: builder.mutation<void, TItemId>({
      query: (id) => ({
        url: `/subscriber/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [ECacheTag.SUBSCRIBERS],
    }),
    saveParameters: builder.mutation<
      TParametersItem[],
      {id: TSubscriber['id']; parameters: TParametersItem[]; isUpdate?: boolean}
    >({
      query: (payload) => ({
        url: `/subscriber/${payload.id}/parameters`,
        method: payload.isUpdate ? 'PUT' : 'POST',
        data: payload.parameters,
      }),
    }),
    getSubscriberParameters: builder.query<TParametersItem[], TItemId>({
      query: (id) => ({
        url: `/subscriber/${id}/parameter`,
        method: 'GET',
      }),
      providesTags: (_, __, id) => [{id, type: ECacheTag.SUBSCRIBERS}],
    }),
    deleteParameters: builder.mutation<
      unknown,
      {id: TSubscriber['id']; parameters: TParametersItem[]}
    >({
      query: (payload) => ({
        url: `/subscriber/${payload.id}/parameters`,
        method: 'DELETE',
        data: payload.parameters,
      }),
    }),
    createTags: builder.mutation<TTagsItem[], {id: TSubscriber['id']; tags: TTagsItem[]}>({
      query: (payload) => ({
        url: `/subscriber/${payload.id}/tags`,
        method: 'POST',
        data: payload.tags,
      }),
    }),
    getSubscriberTags: builder.query<TTagsItem[], TItemId>({
      query: (id) => ({
        url: `/subscriber/${id}/tag`,
        method: 'GET',
      }),
      providesTags: (_, __, id) => [{id, type: ECacheTag.SUBSCRIBERS}],
    }),
    deleteTags: builder.mutation<void, {id: TSubscriber['id']; tags: Omit<TTagsItem, 'id'>[]}>({
      query: (payload) => ({
        url: `/subscriber/${payload.id}/tags`,
        method: 'DELETE',
        data: payload.tags,
      }),
    }),
    getAddress: builder.query<TAddressItem[], TItemId>({
      query: (id) => ({
        url: `/subscriber/${id}/address`,
        method: 'GET',
      }),
      providesTags: (_, __, id) => [{id, type: ECacheTag.SUBSCRIBERS}],
    }),
    getSubscriberPhone: builder.query<string | undefined, TItemId>({
      query: (id) => ({
        url: `/subscriber/${id}/address`,
        method: 'GET',
      }),
      transformResponse: (adresses: TAddressItem[]) =>
        adresses.find((address) => address.type === EAddress.PHONE)?.address ?? undefined,
      providesTags: (_, __, id) => [{id, type: ECacheTag.SUBSCRIBERS}],
    }),
    saveAddress: builder.mutation<
      TAddressItem | TAddressItem[],
      {id: TSubscriber['id']; address: Partial<TAddressItem>; isUpdate?: boolean}
    >({
      query: (payload) => ({
        url: payload.isUpdate
          ? `/subscriber/address/${payload.address.id}`
          : `/subscriber/${payload.id}/address`,
        method: payload.isUpdate ? 'PATCH' : 'POST',
        data: payload.address,
      }),
    }),
    deleteAddress: builder.mutation<void, TAddressItem['id']>({
      query: (id) => ({
        url: `/subscriber/address/${id}`,
        method: 'DELETE',
      }),
    }),
  }),
});

listenerMiddleware.startListening({
  matcher: api.endpoints.uploadSubscribers.matchFulfilled,
  effect: ({payload}) => {
    if (!payload.subscribers) {
      showNewNotification({
        type: 'WARNING',
        messageKey: 'Subscribers:notification.emptyFile',
      });
    }
  },
});

export const {
  useUploadSubscribersMutation,
  useConfirmSubscribersMutation,
  useGetSubscribersUploadStatusQuery,
  useDeleteSubscriberMutation,
  useGetAddressQuery,
  useGetSubscriberParametersQuery,
  useGetSubscriberQuery,
  useGetSubscriberTagsQuery,
  useGetSubscribersQuery,
  useDeleteParametersMutation,
  useCreateTagsMutation,
  useDeleteTagsMutation,
  useDeleteAddressMutation,
  useSaveSubscriberMutation,
  useSaveParametersMutation,
  useSaveAddressMutation,
  useGetSubscriberPhoneQuery,
  useLazyGetSubscriberPhoneQuery,
  useGetSubscriberByAddressQuery,
  endpoints: subscribersEndpoints,
} = api;
